import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import styled, { css, keyframes } from "styled-components";
import { X } from "@phosphor-icons/react";

import { MOBILE_BREAKPOINT } from "../../constants";

// Shared modal shell. Wraps a Radix Dialog with our overlay + content
// chrome, the close-X in the corner, and the animations. Callers render
// their own body (title, content, footer) as children.
//
// Initial focus is suppressed via onOpenAutoFocus(preventDefault) — Radix
// otherwise lands focus on the close-X (it's the first focusable child),
// which is both ugly and a bad UX target. The focus trap stays active, so
// keyboard users still tab into the dialog naturally.
// `className` is forwarded to Content so callers can do
// `styled(Modal)\`...\`` and have the styles land on the actual modal
// box (e.g. EndScreenModal layering a graph-paper pattern over the
// chrome). Without this, styled-components' generated class would be
// dropped on the floor and the override silently no-ops.
function Modal({
  open,
  onOpenChange,
  width = 600,
  topOnMobile = false,
  bottomOnMobile = false,
  className,
  children,
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Overlay />
        <Content
          $width={width}
          $topOnMobile={topOnMobile}
          $bottomOnMobile={bottomOnMobile}
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={className}
        >
          <CloseButton aria-label="Close">
            <X size={18} weight="bold" />
          </CloseButton>
          <Body>{children}</Body>
        </Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translate(-50%, 20%); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;

const Overlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  background: rgba(28, 25, 23, 0.65);
  z-index: 50;
  animation: ${fadeIn} 160ms ease-out;
`;

// max-height: 90dvh (dynamic viewport) so on mobile the modal reclaims
// the room mobile browsers would otherwise hide under their address-bar
// chrome — 90vh undershoots there. Below the modal's own width prop,
// we go full-bleed: 100vw, taller, no rounded corners or border.
const Content = styled(Dialog.Content)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  width: min(100vw, ${(p) => p.$width}px);
  max-height: 90dvh;
  background: var(--color-orange-100);
  border: 1px solid var(--color-orange-300);
  border-radius: 8px;
  box-shadow: var(--surface-box-shadow-strong);
  z-index: 51;
  animation: ${popIn} 180ms ease-out;
  overflow: hidden;

  @media (max-width: ${(p) => p.$width}px) {
    max-height: 96dvh;
    border-radius: 0;
    border: none;
    ${(p) =>
      p.$topOnMobile &&
      `
        top: 0;
        transform: translateX(-50%);
        animation: none;
      `}
  }

  ${(p) =>
    p.$bottomOnMobile &&
    css`
      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        top: auto;
        bottom: 0;
        transform: translateX(-50%);
        animation: ${slideUp} 200ms ease-out;
        max-height: 80dvh;
        border-radius: 12px 12px 0 0;
        border-bottom: none;
      }
    `}
`;

// min-height: 0 lets a flex child shrink below its content size when
// max-height kicks in on Content; without it the body would grow past
// the cap and overflow-y would never trigger. scrollbar-gutter: stable
// reserves the gutter so reflowing in/out of scroll doesn't shift
// content horizontally. Padding clamps from 1.75rem on roomy modals
// down to 1rem on phone widths so the photos/copy aren't crowded by
// chrome at narrow widths.
const Body = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: clamp(1rem, 3vw, 1.75rem);
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: var(--color-orange-300) transparent;
`;

const CloseButton = styled(Dialog.Close)`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--color-text-muted);
  cursor: pointer;

  &:hover {
    background: var(--color-orange-200);
    color: var(--color-text);
  }

  &:focus-visible {
    outline: 2px solid var(--color-orange-700);
    outline-offset: 2px;
  }
`;

// Default title style — borrowed from the original WhatModal: large
// Fraunces serif, dark text. Font-size clamps from 2.5rem on roomy
// modals down to 1.4rem on phone widths so the title doesn't dominate
// the picker grid at narrow viewports. Callers can colorize emphasis
// spans inline (see WhatModal's <EggsTitle> pattern).
export const ModalTitle = styled(Dialog.Title)`
  font-family: "Fraunces", serif;
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0 0 1rem;
  color: var(--color-text);
`;

export default Modal;
